import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVisit } from '../../hooks/useVisits';
import { PageHeader } from '../../components/common/PageHeader';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { ImageGallery } from '../../components/visits/ImageGallery';
import { formatDate } from '../../utils/formatters';

export const VisitDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const visitId = id ? parseInt(id, 10) : 0;

  const navigate = useNavigate();
  const { data: visit, isLoading, error } = useVisit(visitId);

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-12" />;
  }

  if (error || !visit) {
    return <ErrorMessage message={error?.message || 'Visit not found'} />;
  }

  return (
    <div>
      <PageHeader
        title={`Visit - ${formatDate(visit.visitDate)}`}
        actions={
          <button
            type="button"
            onClick={() => navigate(`/prescriptions/new?visitId=${visit.id}`)}
            className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Create Prescription
          </button>
        }
      />

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Visit Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Patient</p>
            <p className="text-lg font-medium">{visit.patientName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Visit Date</p>
            <p className="text-lg font-medium">{formatDate(visit.visitDate)}</p>
          </div>
          {visit.diagnosis && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">Diagnosis</p>
              <p className="text-lg font-medium">{visit.diagnosis}</p>
            </div>
          )}
          {visit.notes && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">Notes</p>
              <p className="text-lg font-medium">{visit.notes}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Visit Images</h2>
        <ImageGallery images={visit.files} />
      </div>
    </div>
  );
};

